import React, { createContext, useContext, useState, useEffect } from 'react';
import { logout as logoutApi } from '../api';
import { fetchAuthContext } from '../api/auth.js';
import { clearUserAndToken, isLoggedIn, getTenantId, getTenantIdFromToken } from '../utils/token.js';

/** 无操作自动退出登录时限（6 小时） */
const IDLE_LOGOUT_MS = 6 * 60 * 60 * 1000;
/** 用户活动重置空闲计时的节流间隔 */
const IDLE_ACTIVITY_THROTTLE_MS = 30 * 1000;
/** Token 过期轮询间隔 */
const TOKEN_CHECK_MS = 30 * 1000;

const UserContext = createContext();

function alignUserTenant(userData) {
  const jwtTid = getTenantIdFromToken();
  if (jwtTid) {
    userData.tenantId = jwtTid;
  } else if (!userData.tenantId) {
    userData.tenantId = getTenantId('default');
  }
  localStorage.setItem('tenantId', String(userData.tenantId).trim());
  return userData;
}

// 导出UserContext
export { UserContext };

export function UserProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  // 初始化时从localStorage加载用户信息
  useEffect(() => {
    const loadUserFromStorage = async () => {
      try {
        setLoading(true);
        const savedUser = localStorage.getItem('user');

        if (savedUser) {
          if (!isLoggedIn()) {
            console.warn('⚠️ 检测到用户信息存在但token无效或已过期，清除用户信息');
            localStorage.removeItem('user');
            clearUserAndToken();
            setUser(null);
            setLoading(false);
            setIsInitialized(true);
            return;
          }

          const userData = alignUserTenant(JSON.parse(savedUser));

          if (userData.username === 'admin' && !userData.userRole) {
            userData.userRole = 'admin';
          }
          localStorage.setItem('user', JSON.stringify(userData));

          setUser(userData);

          try {
            await fetchAuthContext();
          } catch (err) {
            // 后端未启动 / 网关 5xx / 网络断开：无法校验会话，勿停留在欢迎页壳层
            const status = err?.response?.status;
            const unreachable = !err?.response || (typeof status === 'number' && status >= 500);
            if (unreachable) {
              console.warn('认证服务不可用，清除本地会话并回登录:', err);
              localStorage.removeItem('user');
              clearUserAndToken();
              setUser(null);
            } else {
              console.warn('拉取 auth context 失败:', err);
            }
          }
        } else {
          // 如果没有保存的用户信息，清除可能存在的无效数据
          localStorage.removeItem('user');
          setUser(null);
        }
      } catch (error) {
        console.error('加载用户数据失败:', error);
        // 清除损坏的用户数据
        localStorage.removeItem('user');
        setUser(null);
      } finally {
        setLoading(false);
        setIsInitialized(true);
      }
    };

    loadUserFromStorage();
  }, []); // 空依赖数组，只在组件挂载时执行一次

  const updateUser = React.useCallback((userData) => {
    try {
      if (!userData) {
        setUser(null);
        localStorage.removeItem('user');
        return;
      }

      const normalized = alignUserTenant({ ...userData });
      if (normalized.username === 'admin' && !normalized.userRole) {
        normalized.userRole = 'admin';
      }

      const currentUser = user;
      const identityChanged = !currentUser ||
        currentUser.username !== normalized.username ||
        currentUser.id !== normalized.id ||
        currentUser.tenantId !== normalized.tenantId ||
        currentUser.wecomUserId !== normalized.wecomUserId ||
        currentUser.wecomBound !== normalized.wecomBound;

      const profileChanged = !currentUser ||
        identityChanged ||
        currentUser.avatarUrl !== normalized.avatarUrl ||
        currentUser.avatar !== normalized.avatar ||
        currentUser.realName !== normalized.realName ||
        currentUser.email !== normalized.email ||
        currentUser.phone !== normalized.phone;

      if (profileChanged) {
        setUser(normalized);
        localStorage.setItem('user', JSON.stringify(normalized));
        if (identityChanged && isLoggedIn()) {
          fetchAuthContext().catch((err) => console.warn('拉取 auth context 失败:', err));
        }
      }
    } catch (error) {
      console.error('更新用户状态失败:', error);
    }
  }, [user]);

  const logout = React.useCallback(() => {
    // 先本地立即清理，确保“立即退出”生效
    try {
      setUser(null);
      clearUserAndToken();
      sessionStorage.clear();
      // 清除cookies（如果有的话）
      document.cookie.split(";").forEach(function (c) {
        document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      });
    } catch (e) {
      console.error('本地登出清理失败:', e);
    }

    // 后台登出异步触发，不阻塞前端退出
    try {
      const p = logoutApi();
      if (p && typeof p.catch === 'function') p.catch(err => console.warn('登出API调用失败:', err));
    } catch (error) {
      console.warn('触发登出API失败:', error);
    }
  }, []);

  // 已登录：6 小时无操作自动回登录页；Token 过期也立即跳转（无需用户再点操作）
  useEffect(() => {
    if (!user || !isInitialized) return undefined;

    let idleTimer = null;
    let lastActivityReset = 0;
    let lastActivityAt = Date.now();
    let redirected = false;

    const redirectToLogin = () => {
      if (redirected) return;
      redirected = true;
      logout();
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.assign('/login?reason=relogin');
      }
    };

    const armIdleTimer = (delayMs) => {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(redirectToLogin, Math.max(0, delayMs));
    };

    const resetIdleTimer = () => {
      lastActivityAt = Date.now();
      armIdleTimer(IDLE_LOGOUT_MS);
    };

    const onActivity = () => {
      const now = Date.now();
      if (now - lastActivityReset < IDLE_ACTIVITY_THROTTLE_MS) return;
      lastActivityReset = now;
      resetIdleTimer();
    };

    const checkToken = () => {
      if (!isLoggedIn()) {
        redirectToLogin();
      }
    };

    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      checkToken();
      const idleFor = Date.now() - lastActivityAt;
      if (idleFor >= IDLE_LOGOUT_MS) {
        redirectToLogin();
      } else {
        armIdleTimer(IDLE_LOGOUT_MS - idleFor);
      }
    };

    const activityEvents = ['mousedown', 'keydown', 'touchstart', 'scroll', 'mousemove'];
    activityEvents.forEach((evt) => window.addEventListener(evt, onActivity, { passive: true }));
    document.addEventListener('visibilitychange', onVisibility);
    resetIdleTimer();
    checkToken();
    const tokenInterval = setInterval(checkToken, TOKEN_CHECK_MS);

    return () => {
      if (idleTimer) clearTimeout(idleTimer);
      clearInterval(tokenInterval);
      activityEvents.forEach((evt) => window.removeEventListener(evt, onActivity));
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user, isInitialized, logout]);

  return (
    <UserContext.Provider value={{
      user,
      setUser: updateUser,
      logout,
      loading,
      isInitialized
    }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
} 